import { RefLocale } from '../hooks/LanguageHooks';
import { RefStoredValue, restartSurvey, setStoredValue, StateStoredValue } from '../hooks/StoreHooks';
import { useEffect, useState } from 'react';
import * as libCon from './../community-hats-js-library/Constants';
import * as locCon from './../LocalConstants';

import CustomCheckBox from '../elements/CustomCheckBox';
import CustomText from '../elements/CustomText';
import {
  SimpleStoreCopySelectionField,
  SimpleStoreCopySelectionFieldFromId,
} from '../elements/SimpleStoreCopySelectionField';
import {
  dictFromArrays,
  isNullOrUndefined,
  isNullOrUndefinedOrEmpty,
  isNumeric,
  sleep,
} from '../community-hats-js-library/utils/generalFunctions';
import { buildImageUrl, showNotification } from '../utils/generalFunctions';
import CustomButton from '../elements/CustomButton';
import CustomSpin from '../elements/CustomSpin';
import { SimpleStoreCopyFieldFromId } from '../elements/SimpleStoreCopyField';
import { SimpleLikertScale, SimpleLikertScaleFromId } from '../elements/SimpleLikertScale';
import { BloodPressureForm, OralTemperatureForm, ReproductiveCycleStage } from '../components/SurveyComponents';
import { MultipleSurveysProgress, SurveyProgress } from '../hooks/SurveyHooks';
import { buildSurveyExport, constructFollowUpConfiguration } from '../utils/surveyFunctions';
import { uploadToBucket } from '../community-hats-js-library/back-end/router';
import { SimpleMultipleSelectFromId } from './SimpleMultipleSelect';
import { SimpleRadioButtonImagesFromId } from './SimpleRadioButton';
import { SimpleImageUploadFromId } from './SimpleImageUpload';
import { SimpleStoreCopyTextAreaFromId } from './SimpleStoreCopyTextArea';
import { SimpleDatePickerFromId } from './SimpleDatePicker';
import { SimpleSliderFromId } from './SimpleSlider';
import { BackButtonLarge } from './BackButton';
import { RefContextLanguage } from '../providers/LanguageProvider';

const STATE_NOT_STARTED = 'STATE_NOT_STARTED';
const STATE_LOADING_FORM = 'STATE_LOADING_FORM';
const STATE_ANSWERING = 'STATE_ANSWERING';
const STATE_SENDING_ANSWERS = 'STATE_SENDING_ANSWERS';
const STATE_COMPLETED = 'STATE_COMPLETED';
const STATE_FAILED_TO_UPLOAD = 'STATE_FAILED_TO_UPLOAD';

export const QuestionAnsweredComponent = ({ questionId }) => {
  const value = RefStoredValue(questionId);

  const i18n = RefContextLanguage();

  return !isNullOrUndefined(value) ? (
    <div className="horizontalSectionCenter" style={{ marginBottom: '2vh' }}>
      <CustomCheckBox type={locCon.MAIN_PROGRESS_CHECKBOX} checked={true} />
      <div>
        <CustomText type={locCon.SUCCESS_TEXT}>{i18n.t('questionAnsweredSuccessfully')}</CustomText>
      </div>
    </div>
  ) : (
    <div className="horizontalSectionCenter">
      <CustomText type={locCon.DANGER_TEXT}>{i18n.t('questionNotAnswered')}</CustomText>
    </div>
  );
};

export const GenericMultipleSurveysFormStructureButton = ({
  surveyConfigurationsArray,
  configurationStatusId,
  sendingStatusId,
  processConfigurationArray = null,
  onCleanUp = () => true,
  showVersions = true,
}) => {
  const i18n = RefContextLanguage();

  const surveyStatus = RefStoredValue(configurationStatusId);

  const loadSurvey = async () => {
    setStoredValue(sendingStatusId, libCon.STATUS_NOT_STARTED);
    setStoredValue(configurationStatusId, libCon.STATUS_LOADING);

    await sleep(1000);

    if (!isNullOrUndefined(processConfigurationArray)) {
      for (let i = 0; i < processConfigurationArray.length; i++) {
        await processConfigurationArray[i]();
      }
    }

    setStoredValue(configurationStatusId, libCon.STATUS_OK);
    onCleanUp();
  };

  return (
    <div className="verticalSection">
      {showVersions && !isNullOrUndefinedOrEmpty(surveyConfigurationsArray) ? (
        surveyConfigurationsArray.map((surveyConfig, i) => (
          <CustomText type={locCon.SUCCESS_TEXT} key={i}>
            {libCon.SURVEY_METADATA in surveyConfig
              ? `${surveyConfig[libCon.SURVEY_METADATA][libCon.SURVEY_NAME]}: ${surveyConfig[libCon.SURVEY_METADATA][libCon.SURVEY_VERSION]}`
              : '......'}
          </CustomText>
        ))
      ) : (
        <div></div>
      )}

      <div className="verticalSection" style={{ marginTop: '1vh' }}>
        {surveyStatus === libCon.STATUS_LOADING ? (
          <CustomSpin type={locCon.BUTTON_SPINNER} />
        ) : (
          <CustomButton type={locCon.PRIMARY_BUTTON} onClick={() => loadSurvey()}>
            {surveyStatus === libCon.STATUS_NOT_STARTED ? i18n.t('loadFormSurvey') : i18n.t('reloadFormSurvey')}
          </CustomButton>
        )}
      </div>
    </div>
  );
};

export const GenericSurveyFormStructureButton = ({
  surveyConfiguration,
  configurationStatusId,
  sendingStatusId,
  processConfiguration = null,
}) => {
  const i18n = RefContextLanguage();

  const surveyStatus = RefStoredValue(configurationStatusId);

  const loadSurvey = async () => {
    setStoredValue(sendingStatusId, libCon.STATUS_NOT_STARTED);
    setStoredValue(configurationStatusId, libCon.STATUS_LOADING);

    await sleep(1000);

    if (!isNullOrUndefined(processConfiguration)) {
      await processConfiguration();
    }

    setStoredValue(configurationStatusId, libCon.STATUS_OK);
  };

  return (
    <div className="verticalSection">
      <CustomText
        type={locCon.SUCCESS_TEXT}
      >{`${i18n.t('surveyVersion')}: ${libCon.SURVEY_METADATA in surveyConfiguration ? surveyConfiguration[libCon.SURVEY_METADATA][libCon.SURVEY_VERSION] : '......'}`}</CustomText>

      <div className="verticalSection" style={{ marginTop: '1vh' }}>
        {surveyStatus === libCon.STATUS_LOADING ? (
          <CustomSpin type={locCon.BUTTON_SPINNER} />
        ) : (
          <CustomButton type={locCon.PRIMARY_BUTTON} onClick={() => loadSurvey()}>
            {surveyStatus === libCon.STATUS_NOT_STARTED ? i18n.t('loadFormSurvey') : i18n.t('reloadFormSurvey')}
          </CustomButton>
        )}
      </div>
    </div>
  );
};

export const GenericSurveyProgressComponentAndExport = ({
  surveyConfiguration,
  sendingStatusId,
  configurationStatusId,
  buildFilename,
  folderName,
  encoder,
  metaBuilder,
  getSurveySerial,
}) => {
  const i18n = RefContextLanguage();

  const [totalQuestions, answeredQuestions] = SurveyProgress(surveyConfiguration, encoder);

  const [canExport, setCanExport] = useState(() => totalQuestions !== null && totalQuestions <= answeredQuestions);

  const [formPublishedStatus, setFormPublishedStatus] = StateStoredValue(sendingStatusId);
  const [formStatus, setFormStatus] = StateStoredValue(configurationStatusId);

  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setFormPublishedStatus(libCon.STATUS_NOT_STARTED);
  }, [setFormPublishedStatus]);

  useEffect(() => {
    setCanExport(totalQuestions !== null && totalQuestions <= answeredQuestions);
  }, [totalQuestions, answeredQuestions]);

  const localExport = async () => {
    setStoredValue(sendingStatusId, libCon.STATUS_LOADING);

    // Builds
    const toUpload = buildSurveyExport(surveyConfiguration, encoder, metaBuilder);
    const fileName = buildFilename();

    let serial = getSurveySerial();
    if (serial === null) serial = libCon.MISSING;

    // Exports
    const reponse = await uploadToBucket(folderName, serial, fileName, toUpload);

    if (reponse === libCon.OK) {
      setStoredValue(sendingStatusId, libCon.STATUS_OK);

      if (surveyConfiguration !== null && surveyConfiguration !== undefined) restartSurvey(encoder(''));

      setFormStatus(libCon.STATUS_NOT_STARTED);
      setHasError(false);

      showNotification(i18n.t('surveyUploadedCorrectly'));
    } else {
      setHasError(true);
      setStoredValue(sendingStatusId, libCon.STATUS_ERROR);
    }
  };

  const [currentState, setCurrentState] = useState(() => STATE_NOT_STARTED);

  useEffect(() => {
    if (formStatus === libCon.STATUS_NOT_STARTED) {
      if (formPublishedStatus === libCon.STATUS_OK) setCurrentState(STATE_COMPLETED);
      else setCurrentState(STATE_NOT_STARTED);
    } else {
      if (formPublishedStatus === libCon.STATUS_LOADING) setCurrentState(STATE_SENDING_ANSWERS);
      else {
        if (hasError) setCurrentState(STATE_FAILED_TO_UPLOAD);
        else setCurrentState(STATE_ANSWERING);
      }
    }
  }, [surveyConfiguration, formPublishedStatus, hasError, canExport, formStatus]);

  return (
    <RenderProgressComponentAndExport
      currentState={currentState}
      answeredQuestions={answeredQuestions}
      totalQuestions={totalQuestions}
      canExport={canExport}
      localExport={localExport}
    />
  );
};

export const GenericMultipleSurveysProgressComponentAndExport = ({
  surveyConfigurationsArray,
  sendingStatusId,
  configurationStatusId,
  buildFilenamesArray,
  folderNamesArray,
  encodersArray,
  metaBuildersArray,
  getSurveySerialsArray,
  postProcessSurvey,
  onCleanUpArray,
}) => {
  const i18n = RefContextLanguage();

  const [totalQuestions, answeredQuestions] = MultipleSurveysProgress(surveyConfigurationsArray, encodersArray);

  const [canExport, setCanExport] = useState(() => totalQuestions !== null && totalQuestions <= answeredQuestions);

  const [formPublishedStatus, setFormPublishedStatus] = StateStoredValue(sendingStatusId);
  const [formStatus, setFormStatus] = StateStoredValue(configurationStatusId);

  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setFormPublishedStatus(libCon.STATUS_NOT_STARTED);
  }, [setFormPublishedStatus]);

  useEffect(() => {
    setCanExport(totalQuestions !== null && totalQuestions > 0 && totalQuestions <= answeredQuestions);
  }, [totalQuestions, answeredQuestions]);

  const localExport = async () => {
    if (!isNullOrUndefinedOrEmpty(surveyConfigurationsArray)) {
      setStoredValue(sendingStatusId, libCon.STATUS_LOADING);

      let successfulFinish = false;

      for (let i = 0; i < surveyConfigurationsArray.length; i++) {
        let surveyConfiguration = surveyConfigurationsArray[i];

        // Case when no questions are asked
        if (
          !(libCon.SURVEY_STRUCTURE in surveyConfiguration) ||
          surveyConfiguration[libCon.SURVEY_STRUCTURE].length === 0
        ) {
          successfulFinish = true;
          continue;
        }

        let encoder = encodersArray[i];
        let metaBuilder = metaBuildersArray[i];
        let folderName = folderNamesArray[i];
        let buildFilename = buildFilenamesArray[i];
        let getSurveySerial = getSurveySerialsArray[i];

        // Builds
        let toUpload = buildSurveyExport(surveyConfiguration, encoder, metaBuilder);

        // Post Process
        if (!isNullOrUndefined(postProcessSurvey)) toUpload = postProcessSurvey(toUpload);

        const fileName = buildFilename();

        let serial = getSurveySerial();
        if (serial === null) serial = libCon.MISSING;

        // Exports
        const reponse = await uploadToBucket(folderName, serial, fileName, toUpload);

        console.log(i);
        if (reponse === libCon.OK) {
          successfulFinish = true;

          if (!isNullOrUndefined(onCleanUpArray) && !isNullOrUndefined(onCleanUpArray[i])) {
            await onCleanUpArray[i](toUpload);
          }
        } else {
          successfulFinish = false;
          break;
        }
      }

      if (successfulFinish === true) {
        setStoredValue(sendingStatusId, libCon.STATUS_OK);

        // Restarts the questionares
        if (!isNullOrUndefinedOrEmpty(surveyConfigurationsArray)) {
          surveyConfigurationsArray.forEach((surveyConfiguration, i) => {
            if (!isNullOrUndefined(surveyConfiguration)) restartSurvey(encodersArray[i](''));
          });
        }

        setFormStatus(libCon.STATUS_NOT_STARTED);
        setHasError(false);

        showNotification(i18n.t('surveyUploadedCorrectly'));
      } else {
        setHasError(true);
        setStoredValue(sendingStatusId, libCon.STATUS_ERROR);
      }
    }
  };

  const [currentState, setCurrentState] = useState(() => STATE_NOT_STARTED);

  useEffect(() => {
    if (formStatus === libCon.STATUS_NOT_STARTED) {
      if (formPublishedStatus === libCon.STATUS_OK) setCurrentState(STATE_COMPLETED);
      else setCurrentState(STATE_NOT_STARTED);
    } else {
      if (formPublishedStatus === libCon.STATUS_LOADING) setCurrentState(STATE_SENDING_ANSWERS);
      else {
        if (hasError) setCurrentState(STATE_FAILED_TO_UPLOAD);
        else setCurrentState(STATE_ANSWERING);
      }
    }
  }, [surveyConfigurationsArray, formPublishedStatus, hasError, canExport, formStatus]);

  return (
    <RenderProgressComponentAndExport
      currentState={currentState}
      answeredQuestions={answeredQuestions}
      totalQuestions={totalQuestions}
      canExport={canExport}
      localExport={localExport}
    />
  );
};

export const RenderProgressComponentAndExport = ({
  currentState,
  answeredQuestions,
  totalQuestions,
  canExport,
  localExport,
}) => {
  const i18n = RefContextLanguage();

  switch (currentState) {
    case STATE_NOT_STARTED:
      return <CustomText type={locCon.ELEMENT_TITLE_TEXT}>{i18n.t('pleaseLoadFormSurvey')}</CustomText>;
    case STATE_COMPLETED:
      return (
        <div className="verticalSection" style={{ marginTop: 25 }}>
          <CustomText type={locCon.SUCCESS_TEXT}>{i18n.t('formSurveySubmitted')}</CustomText>
          <BackButtonLarge />
        </div>
      );
    case STATE_LOADING_FORM:
      return <div></div>;
    case STATE_SENDING_ANSWERS:
      return (
        <div className="verticalSection">
          <CustomText
            type={locCon.SUCCESS_TEXT}
          >{`Total answered questions: ${answeredQuestions} of ${totalQuestions}`}</CustomText>
          <CustomText type={locCon.DANGER_TEXT}></CustomText>
          <CustomSpin type={locCon.BUTTON_SPINNER} />
        </div>
      );
    case STATE_ANSWERING:
      return (
        <div className="verticalSection" style={{ marginBottom: 5 }}>
          <CustomText
            type={locCon.SUCCESS_TEXT}
          >{`Total answered questions: ${answeredQuestions} of ${totalQuestions}`}</CustomText>
          <CustomText type={locCon.DANGER_TEXT}></CustomText>
          <CustomButton
            type={canExport ? locCon.PRIMARY_BUTTON : locCon.PRIMARY_BUTTON_DISSABLED_BUTTON}
            onClick={() => (canExport ? localExport() : showNotification(i18n.t('pleaseFillAllQuestions')))}
          >
            {i18n.t('sendSurveyForm')}
          </CustomButton>
        </div>
      );
    case STATE_FAILED_TO_UPLOAD:
      return (
        <div className="verticalSection" style={{ marginBottom: 5 }}>
          <CustomText
            type={locCon.SUCCESS_TEXT}
          >{`Total answered questions: ${answeredQuestions} of ${totalQuestions}`}</CustomText>
          <CustomText type={locCon.DANGER_TEXT}>{i18n.t('errorSendingForm')}</CustomText>
          <CustomButton
            type={canExport ? locCon.PRIMARY_BUTTON : locCon.PRIMARY_BUTTON_DISSABLED_BUTTON}
            onClick={() => (canExport ? localExport() : showNotification(i18n.t('pleaseFillAllQuestions')))}
          >
            {i18n.t('sendSurveyForm')}
          </CustomButton>
        </div>
      );
    default:
      return <div></div>;
  }
};

export const RestartSurveyButton = ({
  encodersArray,
  surveyConfigurationsArray,
  onCleanUp = () => true,
  textId = 'restartSurveyForm',
}) => {
  const i18n = RefContextLanguage();

  const restartProcess = (showMessage = true) => {
    if (!isNullOrUndefinedOrEmpty(encodersArray) && !isNullOrUndefinedOrEmpty(surveyConfigurationsArray)) {
      encodersArray.forEach((encoder, i) => {
        if (!isNullOrUndefined(surveyConfigurationsArray[i])) restartSurvey(encoder(''));
      });
    }

    onCleanUp();

    if (showMessage) showNotification(i18n.t('formRestarted'));
  };

  return (
    <div style={{ width: '100%', marginBottom: '1vh', marginTop: '1vh' }}>
      <CustomButton type={locCon.PRIMARY_BUTTON} onClick={() => restartProcess()}>
        {i18n.t(textId)}
      </CustomButton>
    </div>
  );
};

export const GenericMultipleSurveysQuestions = ({
  encodersArray,
  surveyConfigurationsArray,
  configurationStatusId,
  instructionsText,
  includeRestartButton = true,
}) => {
  const surveyStatus = RefStoredValue(configurationStatusId);

  return (
    <div className="verticalSection">
      {surveyStatus === libCon.STATUS_OK ? (
        <div className="verticalSection">
          <div className="horizontalLine" />
          <CustomText type={locCon.INFO_TEXT} style={{ marginBottom: 6 }}>
            {instructionsText}
          </CustomText>
          {includeRestartButton ? (
            <RestartSurveyButton encodersArray={encodersArray} surveyConfigurationsArray={surveyConfigurationsArray} />
          ) : (
            <div></div>
          )}
          <div className="horizontalLine" />
          {surveyConfigurationsArray.map((surveyConfiguration, i) => {
            if (!isNullOrUndefined(surveyConfiguration)) {
              return surveyConfiguration[libCon.SURVEY_STRUCTURE].map((conf, j) => (
                <div className="verticalSection" key={conf[libCon.SURVEY_QUESTION_ID]}>
                  <SurveyComponent
                    configuration={conf}
                    titlePrefix={`${i + 1}.${j + 1}. `}
                    encoder={encodersArray[i]}
                    section={surveyConfiguration[libCon.SURVEY_SECTIONS]?.[conf[libCon.SURVEY_QUESTION_SECTION]]}
                  />
                  <div className="horizontalLine" />
                </div>
              ));
            } else return <div />;
          })}
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
};

export const GenericSurveyQuestions = ({ encoder, surveyConfiguration, configurationStatusId, instructionsText }) => {
  const surveyStatus = RefStoredValue(configurationStatusId);

  const i18n = RefContextLanguage();

  const restartProcess = (showMessage = true) => {
    if (surveyConfiguration !== null && surveyConfiguration !== undefined) restartSurvey(encoder(''));

    if (showMessage) showNotification(i18n.t('formRestarted'));
  };

  return (
    <div className="verticalSection">
      {surveyStatus === libCon.STATUS_OK ? (
        <div className="verticalSection">
          <div className="horizontalLine" />
          <CustomText type={locCon.INFO_TEXT} style={{ marginBottom: 6 }}>
            {instructionsText}
          </CustomText>
          <div style={{ width: '100%', marginBottom: '1vh', marginTop: '1vh' }}>
            <CustomButton type={locCon.PRIMARY_BUTTON} onClick={() => restartProcess()}>
              {i18n.t('restartSurveyForm')}
            </CustomButton>
          </div>
          <div className="horizontalLine" />
          {surveyConfiguration[libCon.SURVEY_STRUCTURE].map((conf, i) => (
            <div className="verticalSection" key={conf[libCon.SURVEY_QUESTION_ID]}>
              <SurveyComponent
                configuration={conf}
                titlePrefix={`${i + 1}. `}
                encoder={encoder}
                section={surveyConfiguration[libCon.SURVEY_SECTIONS]?.[conf[libCon.SURVEY_QUESTION_SECTION]]}
              />
              <div className="horizontalLine" />
            </div>
          ))}
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
};

export const SurveyComponent = ({ configuration, titlePrefix, encoder, section }) => {
  const currentLocale = RefLocale();

  const [currentTranslation, setCurrentTranslation] = useState(() =>
    currentLocale in configuration[libCon.SURVEY_QUESTION_TRANSLATION]
      ? configuration[libCon.SURVEY_QUESTION_TRANSLATION][currentLocale]
      : configuration
  );

  const [currentSectionTranslation, setCurrentSectionTranslation] = useState(() =>
    !isNullOrUndefined(section) && currentLocale in section[libCon.SURVEY_QUESTION_TRANSLATION]
      ? section[libCon.SURVEY_QUESTION_TRANSLATION][currentLocale]
      : section
  );

  useEffect(() => {
    let newTranslation =
      currentLocale in configuration[libCon.SURVEY_QUESTION_TRANSLATION]
        ? configuration[libCon.SURVEY_QUESTION_TRANSLATION][currentLocale]
        : configuration;

    setCurrentTranslation({ ...newTranslation });

    let newSection =
      !isNullOrUndefined(section) && currentLocale in section[libCon.SURVEY_QUESTION_TRANSLATION]
        ? section[libCon.SURVEY_QUESTION_TRANSLATION][currentLocale]
        : section;

    setCurrentSectionTranslation({ ...newSection });
  }, [currentLocale, configuration, section]);

  return (
    <>
      {!isNullOrUndefined(section) &&
      section[libCon.SURVEY_SECTION_START] === configuration[libCon.SURVEY_QUESTION_ID] ? (
        <div className="verticalSection" style={{ marginBottom: '3vh' }}>
          <CustomText type={locCon.MODULE_TITLE_TEXT}>
            {currentSectionTranslation[libCon.SURVEY_QUESTION_TITLE]}
          </CustomText>
          <CustomText type={locCon.INSTRUCTIONS_TEXT}>
            {currentSectionTranslation[libCon.SURVEY_QUESTION_INFO_TEXT]}
          </CustomText>
        </div>
      ) : (
        <div></div>
      )}
      <div className="verticalSection">
        <QuestionComponent
          configuration={configuration}
          currentTranslation={currentTranslation}
          encoder={encoder}
          titlePrefix={titlePrefix}
        />
        <QuestionAnsweredComponent questionId={encoder(configuration[libCon.SURVEY_QUESTION_ID])} />
      </div>

      {!isNullOrUndefined(section) &&
      section[libCon.SURVEY_SECTION_END] === configuration[libCon.SURVEY_QUESTION_ID] ? (
        <div className="verticalSection" style={{ marginBottom: '10vh' }}></div>
      ) : (
        <div></div>
      )}
    </>
  );
};

export const QuestionComponent = ({ configuration, currentTranslation, encoder, titlePrefix }) => {
  if (configuration[libCon.SURVEY_QUESTION_TYPE] === libCon.SURVEY_QUESTION_TYPE_CUSTOM) {
    switch (configuration[libCon.SURVEY_QUESTION_ID]) {
      case libCon.HF_CUSTOM_BLOOD_PRESSURE:
        return (
          <BloodPressureForm
            title={`${titlePrefix}${currentTranslation[libCon.SURVEY_QUESTION_TITLE]}`}
            infoText={currentTranslation[libCon.SURVEY_QUESTION_INFO_TEXT]}
          />
        );
      case libCon.HF_CUSTOM_ORAL_TEMPERATURE:
        return (
          <OralTemperatureForm
            title={`${titlePrefix}${currentTranslation[libCon.SURVEY_QUESTION_TITLE]}`}
            infoText={currentTranslation[libCon.SURVEY_QUESTION_INFO_TEXT]}
          />
        );
      case libCon.PWHF_CUSTOM_REPRODUCTIVE_CYCLE_STAGE:
        return (
          <ReproductiveCycleStage
            title={`${titlePrefix}${currentTranslation[libCon.SURVEY_QUESTION_TITLE]}`}
            infoText={currentTranslation[libCon.SURVEY_QUESTION_INFO_TEXT]}
          />
        );

      default:
        console.error(`No support for custom type: ${configuration[libCon.SURVEY_QUESTION_ID]}`);
        return <div></div>;
    }
  } else {
    switch (configuration[libCon.SURVEY_QUESTION_TYPE]) {
      case libCon.SURVEY_QUESTION_TYPE_MULTIPLE_CHOICE:
        return (
          <QuestionMultipleChoice
            configuration={configuration}
            currentTranslation={currentTranslation}
            encoder={encoder}
            titlePrefix={titlePrefix}
          />
        );

      case libCon.SURVEY_QUESTION_TYPE_BINARY_FOLLOWUP:
        return (
          <QuestionBinaryFollowup
            configuration={configuration}
            currentTranslation={currentTranslation}
            encoder={encoder}
            titlePrefix={titlePrefix}
          />
        );
      case libCon.SURVEY_QUESTION_TYPE_MULTIPLE_FOLLOWUP:
        return (
          <QuestionMultipleChoiceFollowup
            configuration={configuration}
            currentTranslation={currentTranslation}
            encoder={encoder}
            titlePrefix={titlePrefix}
          />
        );

      case libCon.SURVEY_QUESTION_TYPE_MULTIPLE_CHOICE_IMAGES:
        return (
          <QuestionMultipleChoiceImages
            configuration={configuration}
            currentTranslation={currentTranslation}
            encoder={encoder}
            titlePrefix={titlePrefix}
          />
        );

      case libCon.SURVEY_QUESTION_TYPE_BINARY:
        return (
          <QuestionBinary
            configuration={configuration}
            currentTranslation={currentTranslation}
            encoder={encoder}
            titlePrefix={titlePrefix}
          />
        );

      case libCon.SURVEY_QUESTION_TYPE_NUMERIC:
        return (
          <QuestionNumeric
            configuration={configuration}
            currentTranslation={currentTranslation}
            encoder={encoder}
            titlePrefix={titlePrefix}
          />
        );

      case libCon.SURVEY_QUESTION_TYPE_MULTIPLE_CHOICE_AND_ANSWER:
        return (
          <QuestionMultipleChoiceAndAnswer
            configuration={configuration}
            currentTranslation={currentTranslation}
            encoder={encoder}
            titlePrefix={titlePrefix}
          />
        );

      case libCon.SURVEY_QUESTION_TYPE_IMAGE_UPLOAD:
        return (
          <QuestionImageUpload
            configuration={configuration}
            currentTranslation={currentTranslation}
            encoder={encoder}
            titlePrefix={titlePrefix}
          />
        );

      case libCon.SURVEY_QUESTION_TYPE_TEXT_AREA:
        return (
          <QuestionTextArea
            configuration={configuration}
            currentTranslation={currentTranslation}
            encoder={encoder}
            titlePrefix={titlePrefix}
          />
        );

      case libCon.SURVEY_QUESTION_TYPE_DATE:
        return (
          <QuestionDate
            configuration={configuration}
            currentTranslation={currentTranslation}
            encoder={encoder}
            titlePrefix={titlePrefix}
          />
        );

      case libCon.SURVEY_QUESTION_TYPE_NUMERIC_SCALE:
        return (
          <QuestionNumericScale
            configuration={configuration}
            currentTranslation={currentTranslation}
            encoder={encoder}
            titlePrefix={titlePrefix}
          />
        );

      default:
        console.error(`No support for custom type: ${configuration[libCon.SURVEY_QUESTION_TYPE]}`);
        return <div></div>;
    }
  }
};

// Specific Components
export const QuestionMultipleChoice = ({ configuration, currentTranslation, encoder, titlePrefix }) => {
  const i18n = RefContextLanguage();

  return (
    <SimpleStoreCopySelectionFieldFromId
      valueID={encoder(configuration[libCon.SURVEY_QUESTION_ID])}
      title={`${titlePrefix}${currentTranslation[libCon.SURVEY_QUESTION_TITLE]}`}
      infoText={currentTranslation[libCon.SURVEY_QUESTION_INFO_TEXT]}
      options={configuration[libCon.SURVEY_MULTIPLE_CHOICE_OPTIONS]}
      defaultValue={libCon.NA}
      checkboxText={i18n.t('informationNotAvailable')}
      includeCheckbox={
        libCon.SURVEY_QUESTION_INCLUDE_NA in configuration ? configuration[libCon.SURVEY_QUESTION_INCLUDE_NA] : false
      }
      enableCopy={false}
      includeOther={
        libCon.SURVEY_MULTIPLE_CHOICE_INCLUDE_OTHER in configuration &&
        configuration[libCon.SURVEY_MULTIPLE_CHOICE_INCLUDE_OTHER] === true
      }
      optionsTextDict={dictFromArrays(
        configuration[libCon.SURVEY_MULTIPLE_CHOICE_OPTIONS],
        currentTranslation[libCon.SURVEY_MULTIPLE_CHOICE_OPTIONS]
      )}
    />
  );
};

export const QuestionBinaryFollowup = ({ configuration, currentTranslation, encoder, titlePrefix }) => {
  const i18n = RefContextLanguage();

  const [firstQuestionAnswer, setFirstQuestionAnswer] = StateStoredValue(
    encoder(configuration[libCon.SURVEY_QUESTION_ID] + '_' + libCon.SURVEY_BINARY_FOLLOWUP_FIRST_QUESTION)
  );
  const [secondQuestionAnswer, setSecondQuestionAnswer] = StateStoredValue(
    encoder(configuration[libCon.SURVEY_QUESTION_ID] + '_' + libCon.SURVEY_BINARY_FOLLOWUP_SECOND_QUESTION)
  );

  const [showFollowUp, setShowFollowUp] = useState(false);

  useEffect(() => {
    let showAnswer = libCon.SURVEY_BINARY_OPTION_YES;
    if (!isNullOrUndefined(configuration[libCon.SURVEY_QUESTION_SHOW_FOLLOWUP_ON_ANSWER]))
      showAnswer = configuration[libCon.SURVEY_QUESTION_SHOW_FOLLOWUP_ON_ANSWER];

    if (!isNullOrUndefined(firstQuestionAnswer)) {
      // No further questions needed
      if (configuration[showAnswer] !== firstQuestionAnswer) {
        setStoredValue(encoder(configuration[libCon.SURVEY_QUESTION_ID]), {
          [libCon.SURVEY_BINARY_FOLLOWUP_FIRST_QUESTION]: firstQuestionAnswer,
          [libCon.SURVEY_BINARY_FOLLOWUP_SECOND_QUESTION]: libCon.NA,
        });
        setShowFollowUp(false);
      } else {
        if (!isNullOrUndefined(secondQuestionAnswer)) {
          setStoredValue(encoder(configuration[libCon.SURVEY_QUESTION_ID]), {
            [libCon.SURVEY_BINARY_FOLLOWUP_FIRST_QUESTION]: firstQuestionAnswer,
            [libCon.SURVEY_BINARY_FOLLOWUP_SECOND_QUESTION]: secondQuestionAnswer,
          });
        } else {
          setStoredValue(encoder(configuration[libCon.SURVEY_QUESTION_ID]), null);
        }

        setShowFollowUp(true);
      }
    } else {
      setStoredValue(encoder(configuration[libCon.SURVEY_QUESTION_ID]), null);
      setSecondQuestionAnswer(null);
      setShowFollowUp(false);
    }
  }, [firstQuestionAnswer, secondQuestionAnswer, encoder, configuration, setSecondQuestionAnswer]);

  return (
    <>
      <SimpleLikertScale
        value={firstQuestionAnswer}
        setValue={setFirstQuestionAnswer}
        title={`${titlePrefix}${currentTranslation[libCon.SURVEY_QUESTION_TITLE]}`}
        infoText={currentTranslation[libCon.SURVEY_QUESTION_INFO_TEXT]}
        options={[configuration[libCon.SURVEY_BINARY_OPTION_NO], configuration[libCon.SURVEY_BINARY_OPTION_YES]]}
        optionText={[
          currentTranslation[libCon.SURVEY_BINARY_OPTION_NO],
          currentTranslation[libCon.SURVEY_BINARY_OPTION_YES],
        ]}
        checkboxText={i18n.t('informationNotAvailable')}
        includeCheckbox={
          libCon.SURVEY_QUESTION_INCLUDE_NA in configuration ? configuration[libCon.SURVEY_QUESTION_INCLUDE_NA] : false
        }
        defaultValue={libCon.NA}
        includeResponsetext={false}
      />
      {showFollowUp === true ? (
        <QuestionComponent
          configuration={{
            ...constructFollowUpConfiguration(configuration),
            [libCon.SURVEY_QUESTION_ID]:
              configuration[libCon.SURVEY_QUESTION_ID] + '_' + libCon.SURVEY_BINARY_FOLLOWUP_SECOND_QUESTION,
          }}
          currentTranslation={{
            ...constructFollowUpConfiguration(currentTranslation),
            [libCon.SURVEY_QUESTION_TITLE]: i18n.t('aditionalnfo'),
          }}
          encoder={encoder}
          titlePrefix={''}
        />
      ) : (
        <div></div>
      )}
    </>
  );
};

export const QuestionMultipleChoiceFollowup = ({ configuration, currentTranslation, encoder, titlePrefix }) => {
  const i18n = RefContextLanguage();

  const [firstQuestionAnswer, setFirstQuestionAnswer] = StateStoredValue(
    encoder(configuration[libCon.SURVEY_QUESTION_ID] + '_' + libCon.SURVEY_BINARY_FOLLOWUP_FIRST_QUESTION)
  );
  const [secondQuestionAnswer, setSecondQuestionAnswer] = StateStoredValue(
    encoder(configuration[libCon.SURVEY_QUESTION_ID] + '_' + libCon.SURVEY_BINARY_FOLLOWUP_SECOND_QUESTION)
  );

  const [showFollowUp, setShowFollowUp] = useState(false);

  useEffect(() => {
    let showAnswers = [];
    if (!isNullOrUndefined(configuration[libCon.SURVEY_QUESTION_SHOW_FOLLOWUP_ON_ANSWER]))
      showAnswers = configuration[libCon.SURVEY_QUESTION_SHOW_FOLLOWUP_ON_ANSWER];

    if (!isNullOrUndefined(firstQuestionAnswer)) {
      // No further questions needed
      if (!showAnswers.includes(firstQuestionAnswer)) {
        setStoredValue(encoder(configuration[libCon.SURVEY_QUESTION_ID]), {
          [libCon.SURVEY_BINARY_FOLLOWUP_FIRST_QUESTION]: firstQuestionAnswer,
          [libCon.SURVEY_BINARY_FOLLOWUP_SECOND_QUESTION]: libCon.NA,
        });
        setShowFollowUp(false);
      } else {
        if (!isNullOrUndefined(secondQuestionAnswer)) {
          setStoredValue(encoder(configuration[libCon.SURVEY_QUESTION_ID]), {
            [libCon.SURVEY_BINARY_FOLLOWUP_FIRST_QUESTION]: firstQuestionAnswer,
            [libCon.SURVEY_BINARY_FOLLOWUP_SECOND_QUESTION]: secondQuestionAnswer,
          });
        } else {
          setStoredValue(encoder(configuration[libCon.SURVEY_QUESTION_ID]), null);
        }

        setShowFollowUp(true);
      }
    } else {
      setStoredValue(encoder(configuration[libCon.SURVEY_QUESTION_ID]), null);
      setSecondQuestionAnswer(null);
      setShowFollowUp(false);
    }
  }, [firstQuestionAnswer, secondQuestionAnswer, encoder, configuration, setSecondQuestionAnswer]);

  return (
    <>
      <SimpleStoreCopySelectionField
        value={firstQuestionAnswer}
        setValue={setFirstQuestionAnswer}
        title={`${titlePrefix}${currentTranslation[libCon.SURVEY_QUESTION_TITLE]}`}
        infoText={currentTranslation[libCon.SURVEY_QUESTION_INFO_TEXT]}
        options={configuration[libCon.SURVEY_MULTIPLE_CHOICE_OPTIONS]}
        defaultValue={libCon.NA}
        checkboxText={i18n.t('informationNotAvailable')}
        includeCheckbox={
          libCon.SURVEY_QUESTION_INCLUDE_NA in configuration ? configuration[libCon.SURVEY_QUESTION_INCLUDE_NA] : false
        }
        enableCopy={false}
        includeOther={
          libCon.SURVEY_MULTIPLE_CHOICE_INCLUDE_OTHER in configuration &&
          configuration[libCon.SURVEY_MULTIPLE_CHOICE_INCLUDE_OTHER] === true
        }
        optionsTextDict={dictFromArrays(
          configuration[libCon.SURVEY_MULTIPLE_CHOICE_OPTIONS],
          currentTranslation[libCon.SURVEY_MULTIPLE_CHOICE_OPTIONS]
        )}
      />

      {showFollowUp === true ? (
        <QuestionComponent
          configuration={{
            ...constructFollowUpConfiguration(configuration),
            [libCon.SURVEY_QUESTION_ID]:
              configuration[libCon.SURVEY_QUESTION_ID] + '_' + libCon.SURVEY_BINARY_FOLLOWUP_SECOND_QUESTION,
          }}
          currentTranslation={{
            ...constructFollowUpConfiguration(currentTranslation),
            [libCon.SURVEY_QUESTION_TITLE]: i18n.t('aditionalnfo'),
          }}
          encoder={encoder}
          titlePrefix={''}
        />
      ) : (
        <div></div>
      )}
    </>
  );
};

export const QuestionMultipleChoiceImages = ({ configuration, currentTranslation, encoder, titlePrefix }) => {
  const i18n = RefContextLanguage();

  return (
    <SimpleRadioButtonImagesFromId
      valueID={encoder(configuration[libCon.SURVEY_QUESTION_ID])}
      title={`${titlePrefix}${currentTranslation[libCon.SURVEY_QUESTION_TITLE]}`}
      infoText={currentTranslation[libCon.SURVEY_QUESTION_INFO_TEXT]}
      options={configuration[libCon.SURVEY_MULTIPLE_CHOICE_OPTIONS]}
      optionsTextDict={dictFromArrays(
        configuration[libCon.SURVEY_MULTIPLE_CHOICE_OPTIONS],
        currentTranslation[libCon.SURVEY_MULTIPLE_CHOICE_OPTIONS]
      )}
      optionsImagesDict={dictFromArrays(
        configuration[libCon.SURVEY_MULTIPLE_CHOICE_OPTIONS],
        configuration[libCon.SURVEY_MULTIPLE_CHOICE_IMAGES].map((p) => buildImageUrl(p))
      )}
      defaultValue={libCon.NA}
      checkboxText={i18n.t('informationNotAvailable')}
      includeCheckbox={
        libCon.SURVEY_QUESTION_INCLUDE_NA in configuration ? configuration[libCon.SURVEY_QUESTION_INCLUDE_NA] : false
      }
      includeOther={
        libCon.SURVEY_MULTIPLE_CHOICE_INCLUDE_OTHER in configuration &&
        configuration[libCon.SURVEY_MULTIPLE_CHOICE_INCLUDE_OTHER] === true
      }
    />
  );
};

export const QuestionBinary = ({ configuration, currentTranslation, encoder, titlePrefix }) => {
  const i18n = RefContextLanguage();

  return (
    <SimpleLikertScaleFromId
      valueID={encoder(configuration[libCon.SURVEY_QUESTION_ID])}
      title={`${titlePrefix}${currentTranslation[libCon.SURVEY_QUESTION_TITLE]}`}
      infoText={currentTranslation[libCon.SURVEY_QUESTION_INFO_TEXT]}
      options={[configuration[libCon.SURVEY_BINARY_OPTION_NO], configuration[libCon.SURVEY_BINARY_OPTION_YES]]}
      optionText={[
        currentTranslation[libCon.SURVEY_BINARY_OPTION_NO],
        currentTranslation[libCon.SURVEY_BINARY_OPTION_YES],
      ]}
      checkboxText={i18n.t('informationNotAvailable')}
      includeCheckbox={
        libCon.SURVEY_QUESTION_INCLUDE_NA in configuration ? configuration[libCon.SURVEY_QUESTION_INCLUDE_NA] : false
      }
      defaultValue={libCon.NA}
    />
  );
};

export const QuestionNumeric = ({ configuration, currentTranslation, encoder, titlePrefix }) => {
  const i18n = RefContextLanguage();

  const numericCheck = (val) => {
    if (val === libCon.NA) return true;

    if (!isNumeric(val)) return false;

    if (libCon.SURVEY_QUESTION_MIN_VALUE in configuration && val < configuration[libCon.SURVEY_QUESTION_MIN_VALUE])
      return false;

    if (libCon.SURVEY_QUESTION_MAX_VALUE in configuration && val > configuration[libCon.SURVEY_QUESTION_MAX_VALUE])
      return false;

    return true;
  };

  return (
    <SimpleStoreCopyFieldFromId
      valueID={encoder(configuration[libCon.SURVEY_QUESTION_ID])}
      title={`${titlePrefix}${currentTranslation[libCon.SURVEY_QUESTION_TITLE]}`}
      infoText={currentTranslation[libCon.SURVEY_QUESTION_INFO_TEXT]}
      defaultValue={libCon.NA}
      checkboxText={i18n.t('informationNotAvailable')}
      precision={
        libCon.SURVEY_QUESTION_PRECISION in configuration ? configuration[libCon.SURVEY_QUESTION_PRECISION] : 0
      }
      includeCheckbox={
        libCon.SURVEY_QUESTION_INCLUDE_NA in configuration ? configuration[libCon.SURVEY_QUESTION_INCLUDE_NA] : false
      }
      marginBottom={5}
      placeHolder={i18n.t('answer')}
      isInputNumeric={true}
      checkFunction={numericCheck}
    />
  );
};

export const QuestionMultipleChoiceAndAnswer = ({ configuration, currentTranslation, encoder, titlePrefix }) => {
  const i18n = RefContextLanguage();

  let noneText = i18n.t('noneOfTheOptions');
  if (libCon.SURVEY_MULTIPLE_CHOICE_NONE_TEXT in currentTranslation)
    noneText = currentTranslation[libCon.SURVEY_MULTIPLE_CHOICE_NONE_TEXT];

  return (
    <SimpleMultipleSelectFromId
      valueID={encoder(configuration[libCon.SURVEY_QUESTION_ID])}
      title={`${titlePrefix}${currentTranslation[libCon.SURVEY_QUESTION_TITLE]}`}
      infoText={currentTranslation[libCon.SURVEY_QUESTION_INFO_TEXT]}
      options={configuration[libCon.SURVEY_MULTIPLE_CHOICE_OPTIONS]}
      defaultValue={libCon.NA}
      checkboxText={i18n.t('informationNotAvailable')}
      includeNoneOption={
        libCon.SURVEY_QUESTION_INCLUDE_NA in configuration ? configuration[libCon.SURVEY_QUESTION_INCLUDE_NA] : false
      }
      includeOther={
        libCon.SURVEY_QUESTION_INCLUDE_OTHER in configuration
          ? configuration[libCon.SURVEY_QUESTION_INCLUDE_OTHER]
          : false
      }
      enableCopy={false}
      noneOptionText={noneText}
      optionsTextDict={dictFromArrays(
        configuration[libCon.SURVEY_MULTIPLE_CHOICE_OPTIONS],
        currentTranslation[libCon.SURVEY_MULTIPLE_CHOICE_OPTIONS]
      )}
    />
  );
};

export const QuestionImageUpload = ({ configuration, currentTranslation, encoder, titlePrefix }) => {
  const i18n = RefContextLanguage();

  return (
    <SimpleImageUploadFromId
      valueID={encoder(configuration[libCon.SURVEY_QUESTION_ID])}
      title={`${titlePrefix}${currentTranslation[libCon.SURVEY_QUESTION_TITLE]}`}
      infoText={currentTranslation[libCon.SURVEY_QUESTION_INFO_TEXT]}
      infoImage={
        libCon.SURVEY_QUESTION_IMAGE in configuration
          ? buildImageUrl(configuration[libCon.SURVEY_QUESTION_IMAGE])
          : null
      }
      defaultValue={libCon.NA}
      checkboxText={i18n.t('informationNotAvailable')}
      includeCheckbox={
        libCon.SURVEY_QUESTION_INCLUDE_NA in configuration ? configuration[libCon.SURVEY_QUESTION_INCLUDE_NA] : false
      }
    />
  );
};

export const QuestionNumericScale = ({ configuration, currentTranslation, encoder, titlePrefix }) => {
  const i18n = RefContextLanguage();

  return (
    <SimpleSliderFromId
      valueID={encoder(configuration[libCon.SURVEY_QUESTION_ID])}
      title={`${titlePrefix}${currentTranslation[libCon.SURVEY_QUESTION_TITLE]}`}
      infoText={currentTranslation[libCon.SURVEY_QUESTION_INFO_TEXT]}
      minValue={configuration[libCon.SURVEY_QUESTION_MIN_VALUE]}
      maxValue={configuration[libCon.SURVEY_QUESTION_MAX_VALUE]}
      minText={
        libCon.SURVEY_QUESTION_MIN_TEXT in currentTranslation
          ? currentTranslation[libCon.SURVEY_QUESTION_MIN_TEXT]
          : configuration[libCon.SURVEY_QUESTION_MIN_TEXT]
      }
      maxText={
        libCon.SURVEY_QUESTION_MAX_TEXT in currentTranslation
          ? currentTranslation[libCon.SURVEY_QUESTION_MAX_TEXT]
          : configuration[libCon.SURVEY_QUESTION_MAX_TEXT]
      }
      defaultValue={libCon.NA}
      checkboxText={i18n.t('informationNotAvailable')}
      includeCheckbox={
        libCon.SURVEY_QUESTION_INCLUDE_NA in configuration ? configuration[libCon.SURVEY_QUESTION_INCLUDE_NA] : false
      }
    />
  );
};

export const QuestionDate = ({ configuration, currentTranslation, encoder, titlePrefix }) => {
  const i18n = RefContextLanguage();

  return (
    <SimpleDatePickerFromId
      valueID={encoder(configuration[libCon.SURVEY_QUESTION_ID])}
      title={`${titlePrefix}${currentTranslation[libCon.SURVEY_QUESTION_TITLE]}`}
      infoText={currentTranslation[libCon.SURVEY_QUESTION_INFO_TEXT]}
      defaultValue={libCon.NA}
      checkboxText={i18n.t('informationNotAvailable')}
      includeCheckbox={
        libCon.SURVEY_QUESTION_INCLUDE_NA in configuration ? configuration[libCon.SURVEY_QUESTION_INCLUDE_NA] : false
      }
      usePlaceholder={true}
      placeholder={i18n.t('selectDate')}
      dateFormat={'YYYY-MM-DD'}
    />
  );
};

export const QuestionTextArea = ({ configuration, currentTranslation, encoder, titlePrefix }) => {
  const i18n = RefContextLanguage();

  return (
    <SimpleStoreCopyTextAreaFromId
      valueID={encoder(configuration[libCon.SURVEY_QUESTION_ID])}
      title={`${titlePrefix}${currentTranslation[libCon.SURVEY_QUESTION_TITLE]}`}
      infoText={currentTranslation[libCon.SURVEY_QUESTION_INFO_TEXT]}
      defaultValue={libCon.NA}
      checkboxText={i18n.t('nothingToAdd')}
      includeCheckbox={
        libCon.SURVEY_QUESTION_INCLUDE_NA in configuration ? configuration[libCon.SURVEY_QUESTION_INCLUDE_NA] : false
      }
    />
  );
};
